import { useEffect } from 'react';

let sentryInitialized = false;

// Initialize Sentry for error tracking
const useSentryMonitoring = () => {
  useEffect(() => {
    const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
    const SENTRY_ENV = import.meta.env.VITE_SENTRY_ENV || 'production';
    const ENABLE_ERROR_TRACKING = import.meta.env.VITE_ENABLE_ERROR_TRACKING === 'true';

    if (!ENABLE_ERROR_TRACKING || !SENTRY_DSN || sentryInitialized) {
      return;
    }

    // Load Sentry SDK
    const script = document.createElement('script');
    script.src = 'https://browser.sentry-cdn.com/7.99.0/bundle.min.js';
    script.integrity = 'sha384-your-integrity-hash';
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      if (window.Sentry) {
        window.Sentry.init({
          dsn: SENTRY_DSN,
          environment: SENTRY_ENV,
          release: import.meta.env.VITE_SENTRY_RELEASE || '1.0.0',
          integrations: [
            new window.Sentry.BrowserTracing(),
            new window.Sentry.Replay({
              maskAllText: true,
              blockAllMedia: true,
            }),
          ],
          // Performance Monitoring
          tracesSampleRate: SENTRY_ENV === 'production' ? 0.1 : 1.0,
          // Session Replay
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
          // Additional options
          beforeSend(event, hint) {
            // Filter out certain errors
            if (event.exception) {
              const error = hint.originalException;
              // Skip common browser extension errors
              if (error && error.message && error.message.includes('chrome-extension://')) {
                return null;
              }
            }
            return event;
          },
        });

        sentryInitialized = true;
        console.log('Sentry error tracking initialized');
      }
    };
    document.head.appendChild(script);

    // Global error handler
    const handleError = (event) => {
      if (window.Sentry) {
        window.Sentry.captureException(event.error);
      }
    };

    const handleUnhandledRejection = (event) => {
      if (window.Sentry) {
        window.Sentry.captureException(event.reason);
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
};

export default useSentryMonitoring;

// Function to manually capture errors
export const captureError = (error, context = {}) => {
  if (window.Sentry) {
    window.Sentry.captureException(error, {
      contexts: { custom: context },
    });
  } else {
    console.error('Sentry not initialized:', error);
  }
};

// Function to capture messages
export const captureMessage = (message, level = 'info') => {
  if (window.Sentry) {
    window.Sentry.captureMessage(message, level);
  }
};

// Function to set user context
export const setUserContext = (user) => {
  if (window.Sentry) {
    window.Sentry.setUser(user);
  }
};

// Function to add breadcrumb
export const addBreadcrumb = (breadcrumb) => {
  if (window.Sentry) {
    window.Sentry.addBreadcrumb(breadcrumb);
  }
};
